import type { ProductFlyer } from '@avrahami/contracts';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { saveFile } from '../api';
import { Button } from '../components';
import { drawFlyer, FLYER_SIZE, type FlyerImage } from './draw';
import { loadFlyerFonts } from './fonts';
import { standPhoto } from './ground';
import { flyerFileName } from './layout';

/** An image of the store, or null when there is none or it does not load. */
async function loadImage(url: string | null): Promise<FlyerImage | null> {
  if (url === null) return null;
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  try {
    await image.decode();
  } catch {
    return null;
  }
  return { source: image, width: image.naturalWidth, height: image.naturalHeight };
}

export interface FlyerWords {
  download: string;
  drawing: string;
  /** Said by the canvas to a screen reader; `{title}` is the product's title. */
  imageLabel: string;
  downloadFailed: string;
}

/**
 * A product's flyer as an image (docs/FLYERS.md): the flyer of the store's template, drawn in the
 * browser from what the API answers, with a button that saves it as a PNG file of 1080 by 1080
 * pixels: what a store posts on social networks or sends on WhatsApp. `mediaUrl` turns a file name into the admin's own media address.
 */
export function FlyerPreview({
  flyer,
  mediaUrl,
  words,
  actions,
}: {
  flyer: ProductFlyer;
  mediaUrl: (file: string) => string;
  words: FlyerWords;
  /** More buttons next to the download. */
  actions?: ReactNode;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const image = flyer.image === null ? null : mediaUrl(flyer.image);
  const logo = flyer.store.logo === null ? null : mediaUrl(flyer.store.logo);
  const maker = flyer.maker.logo === null ? null : mediaUrl(flyer.maker.logo);

  useEffect(() => {
    let current = true;
    setReady(false);
    void Promise.all([
      loadFlyerFonts(flyer.storeDesign.typeface),
      loadImage(image),
      loadImage(logo),
      loadImage(maker),
    ]).then(([, product, logoImage, makerImage]) => {
      const ctx = canvas.current?.getContext('2d');
      if (!current || !ctx) return;
      drawFlyer(ctx, flyer, {
        product: product === null ? null : standPhoto(product),
        logo: logoImage,
        maker: makerImage,
      });
      setReady(true);
    });
    return () => {
      current = false;
    };
  }, [flyer, image, logo, maker]);

  const download = () => {
    setFailed(false);
    canvas.current?.toBlob((blob) => {
      if (blob === null) {
        setFailed(true);
        return;
      }
      saveFile(blob, flyerFileName(flyer.title));
    }, 'image/png');
  };

  return (
    <div className="flex flex-col gap-3" data-testid="flyer-preview">
      <canvas
        ref={canvas}
        width={FLYER_SIZE}
        height={FLYER_SIZE}
        role="img"
        aria-label={words.imageLabel.replace('{title}', flyer.title)}
        data-ready={ready ? 'true' : 'false'}
        className="aspect-square w-full max-w-[540px] rounded-md bg-white ring-1 ring-slate-200"
      />
      {ready ? null : <p className="text-xs text-slate-500">{words.drawing}</p>}
      {failed ? (
        <p role="alert" className="text-sm text-red-700">
          {words.downloadFailed}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button onClick={download} disabled={!ready} data-testid="flyer-download">
          {words.download}
        </Button>
        {actions}
      </div>
    </div>
  );
}
